package com.example.hukakmp.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.hukakmp.reportchat.ConversationalReportDraft
import com.example.hukakmp.reportchat.conversationalReportAvailable
import com.example.hukakmp.viewmodel.ConversationalReportViewModel
import com.example.hukakmp.voice.VoiceDictation

@Composable
fun ConversationalReportScreen(
    uiState: ConversationalReportViewModel.UiState,
    onAnalyze: (String) -> Unit,
    onClearDraft: () -> Unit,
    onClearError: () -> Unit,
    onContinue: (ConversationalReportDraft) -> Unit,
    onBack: () -> Unit,
) {
    var text by remember { mutableStateOf("") }
    var isListening by remember { mutableStateOf(false) }
    var voiceError by remember { mutableStateOf<String?>(null) }

    DisposableEffect(Unit) {
        onDispose {
            VoiceDictation.stop()
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = {
            TopAppBar(
                navigationIcon = {
                    TextButton(onClick = onBack) {
                        Text("‹", color = MaterialTheme.colorScheme.onPrimary, fontSize = 30.sp)
                    }
                },
                title = {
                    Column {
                        Text("Parte por chat", fontWeight = FontWeight.Bold)
                        Text(
                            "${uiState.chatsRemaining} de ${uiState.chatsTotal} consultas disponibles hoy",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onPrimary.copy(alpha = 0.76f),
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.primary,
                    titleContentColor = MaterialTheme.colorScheme.onPrimary,
                ),
            )
        },
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .verticalScroll(rememberScrollState())
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Surface(
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(20.dp),
                color = MaterialTheme.colorScheme.primaryContainer,
            ) {
                Column(modifier = Modifier.padding(18.dp)) {
                    Text("Contame tu jornada", fontWeight = FontWeight.Bold)
                    Text(
                        "Podés escribirlo como te salga. Huka intenta ordenar los datos y después te lleva al formulario para revisar lo que falte.",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onPrimaryContainer,
                        modifier = Modifier.padding(top = 5.dp),
                    )
                }
            }

            if (!conversationalReportAvailable) {
                Text(
                    "La carga conversacional todavía no está habilitada para esta plataforma KMP.",
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth().padding(vertical = 24.dp),
                )
                return@Column
            }

            OutlinedTextField(
                value = text,
                onValueChange = {
                    text = it
                    if (uiState.error != null) onClearError()
                },
                modifier = Modifier.fillMaxWidth(),
                label = { Text("Relato de la jornada") },
                placeholder = {
                    Text("Ej: ayer salí de costa de 7 a 11, usé dos cañas y saqué 3 pejerreyes")
                },
                minLines = 5,
                maxLines = 9,
                shape = RoundedCornerShape(16.dp),
                enabled = !uiState.isAnalyzing,
            )

            if (VoiceDictation.available) {
                OutlinedButton(
                    onClick = {
                        voiceError = null
                        if (isListening) {
                            VoiceDictation.stop()
                            isListening = false
                        } else {
                            val started = VoiceDictation.start(
                                onResult = { transcript ->
                                    text = if (text.isBlank()) {
                                        transcript
                                    } else {
                                        text + " " + transcript
                                    }
                                },
                                onError = { message ->
                                    voiceError = message
                                },
                                onEnd = {
                                    isListening = false
                                },
                            )
                            isListening = started
                            if (!started) {
                                voiceError = "El dictado no está disponible en este navegador."
                            }
                        }
                    },
                    enabled = !uiState.isAnalyzing,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(15.dp),
                ) {
                    Text(
                        if (isListening) {
                            "■ Detener dictado"
                        } else {
                            "🎤 Dictar jornada"
                        },
                        fontWeight = FontWeight.SemiBold,
                    )
                }

                if (isListening) {
                    Text(
                        "Escuchando… hablá con naturalidad. Cuando termines, la transcripción queda arriba para que la revises.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.secondary,
                        modifier = Modifier.fillMaxWidth(),
                        textAlign = TextAlign.Center,
                    )
                }
            }

            voiceError?.let { message ->
                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(14.dp),
                    color = MaterialTheme.colorScheme.tertiaryContainer,
                ) {
                    Text(
                        message,
                        modifier = Modifier.padding(12.dp),
                        color = MaterialTheme.colorScheme.onTertiaryContainer,
                        style = MaterialTheme.typography.bodySmall,
                    )
                }
            }

            Button(
                onClick = { onAnalyze(text) },
                enabled = text.isNotBlank() && !uiState.isAnalyzing && uiState.chatsRemaining > 0,
                modifier = Modifier.fillMaxWidth().height(52.dp),
                shape = RoundedCornerShape(15.dp),
            ) {
                if (uiState.isAnalyzing) {
                    CircularProgressIndicator(
                        modifier = Modifier.height(20.dp),
                        strokeWidth = 2.dp,
                    )
                    Text("  Entendiendo tu jornada…")
                } else {
                    Text("Ordenar con Huka IA", fontWeight = FontWeight.Bold)
                }
            }

            uiState.error?.let { error ->
                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(14.dp),
                    color = MaterialTheme.colorScheme.errorContainer,
                ) {
                    Text(
                        error,
                        modifier = Modifier.padding(14.dp),
                        color = MaterialTheme.colorScheme.onErrorContainer,
                    )
                }
            }

            uiState.draft?.let { draft ->
                DraftPreview(draft)

                Button(
                    onClick = { onContinue(draft) },
                    modifier = Modifier.fillMaxWidth().height(52.dp),
                    shape = RoundedCornerShape(15.dp),
                ) {
                    Text("Revisar y completar parte", fontWeight = FontWeight.Bold)
                }

                OutlinedButton(
                    onClick = onClearDraft,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(15.dp),
                ) {
                    Text("Volver a interpretar")
                }
            }

            Text(
                "Huka no guarda el parte desde esta pantalla. Siempre vas a revisar los datos en el formulario antes de enviarlos.",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth().padding(top = 4.dp),
            )
        }
    }
}

@Composable
private fun DraftPreview(draft: ConversationalReportDraft) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(18.dp),
        color = MaterialTheme.colorScheme.surface,
        shadowElevation = 1.dp,
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(7.dp),
        ) {
            Text("Huka entendió esto", fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Text(
                draft.resumen,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                style = MaterialTheme.typography.bodyMedium,
            )

            draft.fecha?.let { DraftRow("📅", "Fecha", it) }
            if (!draft.horaInicio.isNullOrBlank() || !draft.horaFin.isNullOrBlank()) {
                DraftRow(
                    "⏰",
                    "Horario",
                    listOfNotNull(draft.horaInicio, draft.horaFin).joinToString(" → "),
                )
            }
            draft.modalidad?.let {
                DraftRow("🎣", "Modalidad", it.displayName)
            }
            draft.numeroCanas?.let {
                DraftRow("🎯", "Cañas", it.toString())
            }
            draft.nombreLugar?.let {
                DraftRow("📍", "Lugar", it)
            }
            if (draft.sinCapturas) {
                DraftRow("🐟", "Capturas", "Sin capturas")
            } else {
                draft.especies.forEach {
                    DraftRow(
                        "🐟",
                        it.especie,
                        "${it.cantidad} capturados · ${it.devueltos} devueltos",
                    )
                }
            }
            draft.observaciones?.let {
                DraftRow("📝", "Notas", it)
            }

            if (draft.latitud.isNullOrBlank() || draft.longitud.isNullOrBlank()) {
                Text(
                    "📍 La ubicación exacta se completa en el mapa del wizard.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.tertiary,
                    modifier = Modifier.padding(top = 5.dp),
                )
            }
        }
    }
}

@Composable
private fun DraftRow(icon: String, label: String, value: String) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.Top,
    ) {
        Text(icon, modifier = Modifier.padding(end = 8.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(label, fontWeight = FontWeight.SemiBold, style = MaterialTheme.typography.bodySmall)
            Text(value, style = MaterialTheme.typography.bodyMedium)
        }
    }
}
